"use client";

import { useCallback, useEffect } from "react";
import { useParams, useSearchParams } from "next/navigation";
import log from "logbeacon";
import { cn } from "utils";
import { useMemoizedFn } from "ahooks";
import {
  OnboardingAgent,
  OnboardingAgentListItem,
} from "../../Portfolio/Onboarding";

import { Reports360 } from "../360Reports/Reports360";
import { ShowOnPcOrMobile } from "../../ShowOnPcOrMobile";
import { SummaryAgent } from "./SummaryAgent/SummaryAgent";
import { MasterAgent } from "./EdggyAgent/EdggyAgent";
import { TAAgent } from "./TAAgent/TAAgent";
import {
  CopilotSender,
  useCopilotSenderTabInputQuestions,
  setCopilotSenderChatRefContent,
} from "./CopilotSender";
import { ClickNotificationInfo } from "@components";
import { useOspApiClientProvider } from "@/src/hooks";
import { getRandomElements, I18nUtil } from "@/src/utils";
interface CopilotAgentProps {
  activeAgent: OnboardingAgentListItem;
  handleAgentChange: (agent: OnboardingAgent) => void;
  displayName: string;
  viewDailyReport?: boolean;
  notificationInfo?: ClickNotificationInfo | null;
}

export const CopilotAgent = ({
  activeAgent,
  handleAgentChange: _handleAgentChange,
  displayName: _displayName,
  viewDailyReport = false,
  notificationInfo,
}: CopilotAgentProps) => {
  const searchParams = useSearchParams();
  const { openDataApi } = useOspApiClientProvider();
  const { locale } = useParams<{ locale: string }>();

  const { questions = [] } = useCopilotSenderTabInputQuestions();

  // 处理 URL 参数中的 question

  const onChartClick = useMemoizedFn(
    async (value: {
      question?: string;
      title?: string;
      desc?: string;
      id?: string;
      sourceLink?: string;
      type?: string;
      link?: string;
    }) => {
      if (!openDataApi) {
        return;
      }
      let newsRecommendQuestion = "";
      // 只有 ALERT 有新闻id
      if (value.type === "ALERT") {
        const recommendContentsResponse =
          await openDataApi.contents.getContents({
            limit: 1,
            offset: 0,
            language: I18nUtil.getBackgroundLanguageKey(locale),
            ids: [value.id || ""],
          });
        newsRecommendQuestion = getRandomElements(
          (recommendContentsResponse?.data?.data?.rows?.[0] as any)
            ?.questions || [],
          1,
        )[0];
        if (newsRecommendQuestion) {
          log.info(
            "[onChartClick][trace][quotes]",
            newsRecommendQuestion,
            value.id,
            recommendContentsResponse?.data?.data?.rows,
          );
        }
      }
      if (!newsRecommendQuestion) {
        log.info("[onChartClick][trace][default]", value.question, value.id);
      }
      // 使用 PubSub 事件总线，无需 ref
      setCopilotSenderChatRefContent({
        refId: value.id ?? "",
        question: newsRecommendQuestion || value.question || "",
        title: value.title ?? "",
        desc: value.desc ?? "",
        type: value.type ?? "",
        link: value.link ?? "",
        autoSubmit: true,
      });
    },
  );
  useEffect(() => {
    const questionFillParam = searchParams?.get("questionFill");
    if (questionFillParam) {
      onChartClick({ question: questionFillParam });
      return;
    }
  }, [searchParams, onChartClick]);

  const renderPanel = useCallback(() => {
    log.debug("[文案]agentName", activeAgent);
    switch (activeAgent?.label) {
      case "edggy":
        return (
          <MasterAgent
            agentId={activeAgent?.id || ""}
            onChartClick={onChartClick}
            agentLabel={activeAgent?.label || "default"}
            agentName={activeAgent?.name || ""}
            viewDailyReport={viewDailyReport}
            notificationInfo={notificationInfo}
          />
        );
      case "technicals":
        return (
          <TAAgent
            agentId={activeAgent?.id || ""}
            agentName={activeAgent?.name || ""}
            onChartClick={onChartClick}
          />
        );
      case "360":
        return <Reports360 agentId={activeAgent?.id || ""} />;
      default:
        return (
          <SummaryAgent
            agentId={activeAgent?.id || ""}
            onChartClick={onChartClick}
            category={activeAgent?.categories?.[0]?.split("&") || []}
          />
        );
    }
  }, [onChartClick, activeAgent, notificationInfo, viewDailyReport]);

  return (
    <div
      className={cn("relative w-full max-w-[980px] flex flex-col h-full")}
      data-container="main-content"
    >
      <div className="flex flex-col flex-1 h-full min-h-0">{renderPanel()}</div>
      <ShowOnPcOrMobile hideOnMobileApp>
        {/* 移除 ref，不再需要 */}
        <CopilotSender
          questions={questions}
          className="absolute md:bottom-6 bottom-4 right-0 z-10 mt-6"
        />
      </ShowOnPcOrMobile>
    </div>
  );
};
